import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Sparkles } from 'lucide-react';

const FUNCTION_OPTIONS = ['chat', 'image', 'video', 'music', 'code', 'research'];
const STATUS_OPTIONS = ['active', 'inactive', 'beta'];
const COST_TIER_OPTIONS = ['free', 'standard', 'premium'];

export function ModelForm({ model, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    model_id: '',
    provider: '',
    tier: '',
    status: 'active',
    functions: [],
    endpoint_type: '',
    location: '',
    use_publishers_path: false,
    api_endpoint: '',
    enable_search: false,
    cost_tier: 'standard',
    performance_rating: 5
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (model) {
      setFormData({
        name: model.name || '',
        model_id: model.model_id || '',
        provider: model.provider || '',
        tier: model.tier || '',
        status: model.status || 'active',
        functions: model.functions || [],
        endpoint_type: model.endpoint_type || '',
        location: model.location || '',
        use_publishers_path: model.use_publishers_path || false,
        api_endpoint: model.api_endpoint || '',
        enable_search: model.enable_search || false,
        cost_tier: model.cost_tier || 'standard',
        performance_rating: model.performance_rating || 5
      });
    }
  }, [model]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleFunction = (func) => {
    setFormData(prev => ({
      ...prev,
      functions: prev.functions.includes(func)
        ? prev.functions.filter(f => f !== func)
        : [...prev.functions, func]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      await onSave(formData);
    } catch (err) {
      setError(err.message || 'Failed to save model');
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center p-6 z-50"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: "spring", duration: 0.5 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="relative px-8 pt-8 pb-6 border-b border-slate-100">
          <button
            onClick={onCancel}
            className="absolute top-6 right-6 p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-1">
            {model ? 'Edit Model' : 'Advanced Configuration'}
          </h2>
          <p className="text-slate-500 text-sm">
            Complete manual configuration with all options
          </p>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-8 space-y-8">
          {/* Basic Info Section */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center shadow-lg shadow-indigo-500/30">
                <span className="text-white font-black text-sm">1</span>
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900">Basic Information</h3>
                <p className="text-xs text-slate-500 font-medium">Model name, ID, and provider details</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-11">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2.5 uppercase tracking-wider">
                  Model Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., Gemini 3 Pro"
                  className="w-full px-4 py-3 bg-slate-50/50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all font-medium"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2.5 uppercase tracking-wider">
                  Model ID *
                </label>
                <input
                  type="text"
                  value={formData.model_id}
                  onChange={(e) => handleChange('model_id', e.target.value)}
                  placeholder="e.g., gemini-3-pro-preview"
                  className="w-full px-4 py-3 bg-slate-50/50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all font-medium"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2.5 uppercase tracking-wider">
                  Provider *
                </label>
                <input
                  type="text"
                  value={formData.provider}
                  onChange={(e) => handleChange('provider', e.target.value)}
                  placeholder="e.g., google, anthropic, openai"
                  className="w-full px-4 py-3 bg-slate-50/50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all font-medium"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2.5 uppercase tracking-wider">
                  Tier
                </label>
                <input
                  type="text"
                  value={formData.tier}
                  onChange={(e) => handleChange('tier', e.target.value)}
                  placeholder="e.g., Overall Leader"
                  className="w-full px-4 py-3 bg-slate-50/50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2.5 uppercase tracking-wider">
                  Status *
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50/50 border-2 border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all font-semibold"
                >
                  {STATUS_OPTIONS.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2.5 uppercase tracking-wider">
                  Performance Rating (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.performance_rating}
                  onChange={(e) => handleChange('performance_rating', parseInt(e.target.value))}
                  className="w-full px-4 py-3 bg-slate-50/50 border-2 border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all font-medium"
                />
              </div>
            </div>
          </div>

          {/* Functions Section */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/30">
                <span className="text-white font-black text-sm">2</span>
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900">Capabilities</h3>
                <p className="text-xs text-slate-500 font-medium">Select the functions this model supports</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pl-11">
              {FUNCTION_OPTIONS.map(func => (
                <button
                  key={func}
                  type="button"
                  onClick={() => toggleFunction(func)}
                  className={`px-4 py-3 rounded-xl font-bold transition-all ${
                    formData.functions.includes(func)
                      ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white shadow-lg shadow-indigo-500/30'
                      : 'bg-slate-50 border-2 border-slate-200 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {func}
                </button>
              ))}
            </div>
          </div>

          {/* Technical Config Section */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <span className="text-white font-black text-sm">3</span>
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900">Technical Configuration</h3>
                <p className="text-xs text-slate-500 font-medium">API endpoints and advanced settings</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-11">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2.5 uppercase tracking-wider">
                  Endpoint Type
                </label>
                <input
                  type="text"
                  value={formData.endpoint_type}
                  onChange={(e) => handleChange('endpoint_type', e.target.value)}
                  placeholder="e.g., generateContent, predict"
                  className="w-full px-4 py-3 bg-slate-50/50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2.5 uppercase tracking-wider">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="e.g., us-central1, global"
                  className="w-full px-4 py-3 bg-slate-50/50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all font-medium"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-2.5 uppercase tracking-wider">
                  API Endpoint (for custom providers)
                </label>
                <input
                  type="text"
                  value={formData.api_endpoint}
                  onChange={(e) => handleChange('api_endpoint', e.target.value)}
                  placeholder="https://api.example.com/v1"
                  className="w-full px-4 py-3 bg-slate-50/50 border-2 border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all font-medium"
                />
              </div>

              <div className="flex gap-6 md:col-span-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.use_publishers_path}
                    onChange={(e) => handleChange('use_publishers_path', e.target.checked)}
                    className="w-5 h-5 rounded bg-slate-50 border-2 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-slate-700 font-semibold text-sm">Use Publishers Path</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.enable_search}
                    onChange={(e) => handleChange('enable_search', e.target.checked)}
                    className="w-5 h-5 rounded bg-slate-50 border-2 border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-slate-700 font-semibold text-sm">Enable Search</span>
                </label>
              </div>
            </div>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl">
              <p className="text-red-600 text-sm font-semibold">{error}</p>
            </div>
          )}
          </div>

          {/* Footer Actions */}
          <div className="px-8 py-6 border-t border-slate-100 bg-slate-50/50">
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2.5 bg-white hover:bg-slate-50 border-2 border-slate-200 rounded-lg text-slate-700 font-semibold transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-8 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{model ? 'Update' : 'Create'} Model</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
